import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import type { Category } from '@/types/assessment';

interface CategoryTableProps {
  categories: Category[];
  onMenuClick: (event: React.MouseEvent<HTMLElement>, category: Category) => void;
}

export const CategoryTable: React.FC<CategoryTableProps> = ({
  categories,
  onMenuClick,
}) => {
  const theme = useTheme();

  if (!categories || categories.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No categories available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first category to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Created Date</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((category) => (
            <TableRow key={category._id} hover>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {category.name}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {category.description || '-'}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Chip
                  label={category.isActive ? 'Active' : 'Inactive'}
                  color={category.isActive ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {category.createdBy.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {category.createdBy.email}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {new Date(category.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(category.createdAt).toLocaleTimeString()}
                </Typography>
              </TableCell>
              
              <TableCell>
                <IconButton onClick={(e) => onMenuClick(e, category)}>
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};